import {toPennies, formatCurrency, titleCase} from '../utils';

let productUtils = {

    makeSkuQuery (skus) {
        let skuArray = [];
        for (let sku of skus) {
            skuArray.push(`sku=${sku}`);
        }
        return skuArray.join('&');
    },

    getImageUrl (sku, width = 100, type = 'hero') {
        let bucket = type === 'cover' ? 'ppg_cover' : 'heroshots';
        return `//images.playaway.com/v1/image/${bucket}/${sku}?width=${width}`;
    },

    getContractDiscount (prod, contracts = {}) {
        const format = prod.pricing_group || prod.format;
        return (contracts[format] || 0);
    },

    getListPrice (product, currency = 'USD') {
        return currency === 'CAD' ? (parseFloat(product.canadaprice || 0.0)).toFixed(2) : (parseFloat(product.list_price || 0.0)).toFixed(2);
    },

    getFinalPrice (product, discountPercent = 0, currency = 'USD') {
        let listPrice = toPennies(this.getListPrice(product, currency)) || 0;
        let amt = (listPrice * discountPercent).toFixed(0);
        let discount = product.discount ? toPennies(product.discount.toFixed(2)) : toPennies(amt);
        return toPennies(listPrice - discount);
    },

    getTotal (product, discountPercent = 0, currency = 'USD') {
        return this.getFinalPrice(product, discountPercent, currency) * (parseInt(product.qty, 10) || 0);
    },

    getOrderStub (product, discount = 0, currency = 'USD') {
        let finalPrice = this.getFinalPrice(product, discount, currency);
        let listPrice = productUtils.getListPrice(product, currency);
        let subTotal = toPennies(listPrice) * parseInt(product.qty, 10);
        let grandTotal = finalPrice * parseInt(product.qty, 10);
        return {
            _id: product._id,
            format: product.format,
            qty: parseInt(product.qty, 10),
            title: product.title || product.name,
            isbn: product.isbn,
            authors: product.format !== 'lock' && product.authors ? product.authors.join(', ') : '',
            studio: product.studio || '',
            price: formatCurrency(toPennies(finalPrice)),
            list_price: listPrice,
            unit_discount: formatCurrency(toPennies(listPrice) - finalPrice),
            discount: formatCurrency(subTotal - grandTotal),
            sku: product.sku,
            total: formatCurrency(grandTotal),
            allocations: product.allocations || [],
            internalid: product.internalid,
            currency
        };
    },

    getAudience (product) {
        switch (product.format) {
            case 'playaway':
            case 'bookpack':
            case 'recorded-books':
                return this.formatGradeLevel(product.grade_level);
            default:
                return this.formatAgeLevel(product.age_level);
        }
    },

    formatAgeLevel (ageLevel) {
        if (!ageLevel) {
            return 'Ages Unknown';
        }
        if (typeof ageLevel === 'string') { return ageLevel; }

        let hasUpper = ageLevel[1] && ageLevel[1] < 100;
        let upper = !hasUpper ? '+' : ` - ${ageLevel[1]}`;

        if (ageLevel[0] === 0 && !hasUpper) {
            return 'All Ages';
        }
        return `Ages ${ageLevel[0]}${upper}`;
    },

    formatGradeLevel (grade) {
        if (!grade) {
            return 'Grades Unknown';
        }
        if (typeof grade === 'string') { return grade; }

        let hasUpper = grade[1] && grade[1] < 100;
        let upper = !hasUpper ? '+' : ` - ${grade[1]}`;

        if (grade[0] === 0 && !hasUpper) {
            return 'All Grades';
        }
        if (grade[0] > 12) {
            return 'Adult';
        }
        return `Grades ${grade[0]}${upper}`;
    },

    hasWarningLabel (product) {
        // Bail if this is not a playaway, view, launchpad, or bookpack.
        if (!['playaway', 'view', 'launchpad', 'bookpack'].includes(product.format)) {
            return false;
        }

        let age = product.age_level ? product.age_level[product.age_level.length - 1] < 10 : false;
        let grade = product.grade_level ? product.grade_level[product.grade_level.length - 1] < 8 : false;

        return product.age_level !== 'Adult' && (age || grade);
    },

    demangle (title) {
        if (!title) {
            return '';
        }
        // "Hunger Games, The" -> "The Hunger Games"
        var articles = ['The', 'An', 'A'];
        var fragments = title.split(': ');
        // accounts for "Shell Game, The: A Fox and O'Hare Short Story"
        fragments.forEach((fragment, i) => {
            articles.forEach((article) => {
                var splitIndex = (article.length + 2) * -1; // +2 adds room for ", " — negative means start from the end of the string
                if (fragment.slice(splitIndex) === ', ' + article) {
                    var newFragment = article + ' ' + fragment.slice(0, splitIndex);
                    fragments[i] = newFragment;
                }
            });
        });
        return fragments.join(': ');
    },

    getRating (product, territory) {
        if (territory === 'CA') {
            return product.rating_can;
        }
        return product.rating;
    },

    getProductLine (product) {
        let subtype = '';
        if (product.format === 'launchpad' && ['Video Pack'].includes(product.item_type)) {
            subtype = product.item_type;
        }
        return (titleCase(`${product.format} ${subtype}`).trim());
    },
    formatListValue (brand) {
        if (!brand) return [];
        if (typeof brand === 'string') return [brand];
        if (Array.isArray(brand)) return brand.filter(v => v);
        return brand;
    }
};

export default productUtils;
